import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const ActivityLogTab = ({ token, API_URL, companyProfile, showToast }) => {
    const { t } = useLanguage();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sectionFilter, setSectionFilter] = useState('all');
    const [selectedLog, setSelectedLog] = useState(null); // For details modal

    const loadLogs = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/activity-log`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const json = await res.json();
            if (json.success) {
                setLogs(json.data || []);
                localStorage.setItem('redsorm_activity_logs', JSON.stringify(json.data || []));
            } else {
                showToast(json.message || 'Failed to load activity logs', 'error');
            }
        } catch (err) {
            console.error('Error loading activity logs:', err);
            const cached = localStorage.getItem('redsorm_activity_logs');
            if (cached) {
                setLogs(JSON.parse(cached));
            } else {
                showToast('Failed to load activity logs', 'error');
            }
        } finally {
            setIsLoading(false);
        }
    }, [API_URL, token, showToast]);

    useEffect(() => {
        if (token) {
            loadLogs();
        }

        const handleRefreshEvent = () => {
            loadLogs();
        };

        window.addEventListener('refreshActivityLog', handleRefreshEvent);
        return () => {
            window.removeEventListener('refreshActivityLog', handleRefreshEvent);
        };
    }, [token, loadLogs]);

    const formatTimestamp = (isoString) => {
        try {
            const date = new Date(isoString);
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true
            });
        } catch (e) {
            return isoString;
        }
    };

    // Filter logs
    const filteredLogs = logs.filter(log => {
        const matchesSearch = 
            (log.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.user_email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (log.action || '').toLowerCase().includes(searchTerm.toLowerCase());
            
        const matchesSection = sectionFilter === 'all' || log.section === sectionFilter;
        
        return matchesSearch && matchesSection;
    });

    // Extract unique sections for filter dropdown
    const sections = ['all', ...new Set(logs.map(log => log.section).filter(Boolean))];

    return (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-lg p-6 expand-in" style={{ animationDelay: '0.3s' }}>
            {/* Header section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6 pb-6 border-b border-gray-50">
                <div>
                    <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                        <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center border border-emerald-100 shadow-sm">
                            <i className="fas fa-history text-lg"></i>
                        </div>
                        <span>{t('activityLog') || 'Activity Log'}</span>
                    </h2>
                    <p className="text-xs text-gray-500 mt-1">
                        Track all dashboard operations and changes performed by management.
                    </p>
                </div>
            </div>

            {/* Filter controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <div className="flex-1 relative">
                    <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                    <input 
                        type="text"
                        placeholder="Search logs by user or action..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 text-xs font-semibold rounded-xl border border-gray-200 outline-none focus:border-emerald-500 bg-gray-50/50"
                    />
                </div>
                <div className="w-full sm:w-48 relative">
                    <select
                        value={sectionFilter}
                        onChange={(e) => setSectionFilter(e.target.value)}
                        className="w-full px-3 py-2 text-xs font-bold rounded-xl border border-gray-200 outline-none focus:border-emerald-500 bg-gray-50/50 capitalize appearance-none cursor-pointer pr-10"
                    >
                        {sections.map(sec => (
                            <option key={sec} value={sec}>
                                {sec === 'all' ? 'All Sections' : sec}
                            </option>
                        ))}
                    </select>
                    <i className="fas fa-chevron-down absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] pointer-events-none"></i>
                </div>
            </div>

            {/* Table */}
            <div className="overflow-hidden border border-gray-100 rounded-xl bg-gray-50/20 shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-50 border-b border-gray-100">
                            <tr>
                                <th className="p-4 font-semibold text-gray-600 w-44">Timestamp</th>
                                <th className="p-4 font-semibold text-gray-600 w-52">User</th>
                                <th className="p-4 font-semibold text-gray-600 w-28">Section</th>
                                <th className="p-4 font-semibold text-gray-600">Action</th>
                                <th className="p-4 font-semibold text-gray-600 text-center w-20">Details</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {isLoading ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-400 font-semibold">
                                        <i className="fas fa-circle-notch animate-spin mr-2"></i> Loading logs...
                                    </td>
                                </tr>
                            ) : filteredLogs.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="p-8 text-center text-gray-400 font-semibold bg-white">
                                        No activity logs found
                                    </td>
                                </tr>
                            ) : (
                                filteredLogs.map(log => {
                                    const roleColors = {
                                        admin: 'bg-emerald-50 text-emerald-700 border-emerald-100',
                                        owner: 'bg-indigo-50 text-indigo-700 border-indigo-100',
                                        superadmin: 'bg-rose-50 text-rose-700 border-rose-100'
                                    };
                                    const roleClass = roleColors[(log.user_role || '').toLowerCase()] || 'bg-gray-50 text-gray-600 border-gray-100';

                                    const secColors = {
                                        Menu: 'bg-emerald-50 text-emerald-700',
                                        Tables: 'bg-blue-50 text-blue-700',
                                        'Table Groups': 'bg-indigo-50 text-indigo-700',
                                        Staff: 'bg-purple-50 text-purple-700',
                                        Settings: 'bg-amber-50 text-amber-700',
                                        Users: 'bg-teal-50 text-teal-700',
                                        Roles: 'bg-rose-50 text-rose-700',
                                        Branches: 'bg-cyan-50 text-cyan-700'
                                    };
                                    const secClass = secColors[log.section] || 'bg-gray-100 text-gray-700';

                                    return (
                                        <tr key={log.id} className="hover:bg-gray-50/50 transition">
                                            <td className="p-4 text-gray-500 font-medium font-mono">
                                                {formatTimestamp(log.created_at)}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex flex-col">
                                                    <span className="font-bold text-gray-900 flex items-center gap-1.5">
                                                        {log.user_name}
                                                        <span className={`text-[9px] px-2 py-0.5 rounded-full border font-black uppercase ${roleClass}`}>
                                                            {log.user_role}
                                                        </span>
                                                    </span>
                                                    <span className="text-[10px] text-gray-400 font-medium mt-0.5">{log.user_email}</span>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`px-2 py-1 rounded-lg text-[10px] font-bold ${secClass}`}>
                                                    {log.section}
                                                </span>
                                            </td>
                                            <td className="p-4 font-semibold text-gray-700">
                                                {log.action}
                                            </td>
                                            <td className="p-4 text-center">
                                                {log.details ? (
                                                    <button 
                                                        onClick={() => setSelectedLog(log)}
                                                        className="w-7 h-7 bg-gray-50 hover:bg-emerald-50 text-gray-500 hover:text-emerald-600 rounded-lg flex items-center justify-center transition border border-gray-100"
                                                        title="View Details"
                                                    >
                                                        <i className="fas fa-eye text-xs"></i>
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-300">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Details Modal */}
            {selectedLog && (
                <div className="fixed inset-0 modal-overlay flex items-center justify-center p-4 z-[300]" onClick={() => setSelectedLog(null)}>
                    <div className="bg-white rounded-2xl max-w-lg w-full p-6 modal-content shadow-2xl border border-gray-100" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-between items-center pb-4 mb-4 border-b border-gray-100">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                <i className="fas fa-info-circle text-emerald-600"></i>
                                <span>Change Log Details</span>
                            </h3>
                            <button onClick={() => setSelectedLog(null)} className="text-gray-400 hover:text-gray-600 transition">
                                <i className="fas fa-times text-lg"></i>
                            </button>
                        </div>
                        
                        <div className="space-y-4 text-xs">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">Timestamp</span>
                                    <span className="font-semibold text-gray-800">{formatTimestamp(selectedLog.created_at)}</span>
                                </div>
                                <div>
                                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">Section</span>
                                    <span className="font-semibold text-gray-800">{selectedLog.section}</span>
                                </div>
                            </div>
                            
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">Operator</span>
                                    <span className="font-semibold text-gray-800">{selectedLog.user_name} ({selectedLog.user_role})</span>
                                </div>
                                <div>
                                    <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">Email</span>
                                    <span className="font-semibold text-gray-800">{selectedLog.user_email}</span>
                                </div>
                            </div>

                            <div>
                                <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">Action Description</span>
                                <div className="p-3 bg-gray-50 border border-gray-100 rounded-xl font-bold text-gray-800 text-sm">
                                    {selectedLog.action}
                                </div>
                            </div>

                            {(() => {
                                try {
                                    const detailsObj = typeof selectedLog.details === 'string' ? JSON.parse(selectedLog.details) : selectedLog.details;
                                    if (detailsObj) {
                                        return (
                                            <>
                                                <div className="grid grid-cols-2 gap-4">
                                                    {detailsObj.ip && (
                                                        <div>
                                                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">IP Address</span>
                                                            <span className="font-mono text-gray-700 font-bold">{detailsObj.ip}</span>
                                                        </div>
                                                    )}
                                                    {detailsObj.userAgent && (
                                                        <div>
                                                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider">User Agent</span>
                                                            <span className="truncate block font-semibold text-gray-600" title={detailsObj.userAgent}>{detailsObj.userAgent}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                {detailsObj.body && Object.keys(detailsObj.body).length > 0 && (
                                                    <div>
                                                        <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">Submitted Values (JSON)</span>
                                                        <pre className="p-3 bg-slate-900 text-slate-100 font-mono rounded-xl overflow-x-auto text-[10px] max-h-40 leading-normal">
                                                            {JSON.stringify(detailsObj.body, null, 2)}
                                                        </pre>
                                                    </div>
                                                )}
                                            </>
                                        );
                                    }
                                } catch (e) {
                                    return (
                                        <div>
                                            <span className="block text-[10px] font-black text-gray-400 uppercase tracking-wider mb-1">Details Data</span>
                                            <pre className="p-3 bg-gray-50 border border-gray-100 rounded-xl overflow-x-auto font-mono text-[10px] max-h-40">
                                                {selectedLog.details}
                                            </pre>
                                        </div>
                                    );
                                }
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ActivityLogTab;
